import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../components/Button";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, set } from "firebase/database";
import { getAuth } from "firebase/auth";
import { app } from "../firebase";
import axios, { AxiosError } from "axios";
import { loginHeader } from "../utils/loginHeader";
import InfoModal from "../components/OnshowModal";
import StreamAnimation from "../components/StreamLoading";
import { trackClick } from "../utils/analytics";
import { useStreams } from "../hooks/useStreams";
import { eventDateProblem, phoneProblem, todayLocal } from "../utils/eventDetails";

const OnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const auth = getAuth(app);
  const navigate = useNavigate();
  const { reload } = useStreams();
  const [user] = useAuthState(auth);

  // Form State
  const [name, setName] = useState("");
  const [imageLink, setImageLink] = useState("");
  const [color, setColor] = useState("");
  // Optional: so we can help with their event.
  const [phone, setPhone] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [showDetailErrors, setShowDetailErrors] = useState(false);

  // UI State
  const [localError, setLocalError] = useState("");
  const [imageError, setImageError] = useState(false); // Track if logo failed to load
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [buttonText, setButtonText] = useState(t("onboarding.submitCustomize"));

  // Parsing URL args
  const arg = new URLSearchParams(window.location.search);

  // Effect to update button text based on input
  useEffect(() => {
    if (name.trim() !== "" || imageLink.trim() !== "" || color.trim() !== "") {
      setButtonText(t("onboarding.submit"));
    } else {
      setButtonText(t("onboarding.submitCustomize"));
    }
  }, [name, imageLink, color, t]);

  // --- HANDLERS ---

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // 1. Check for spaces
    if (value.includes(" ")) {
      setLocalError(t("onboarding.streamNameError") || "Stream names cannot contain spaces.");
    }
    // 2. Check length (if not empty)
    else if (value.length > 0 && value.length < 3) {
      setLocalError("Stream name must be at least 3 characters.");
    }
    // 3. Valid
    else {
      setLocalError("");
    }

    setName(value);
  };

  const handleImageLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageLink(e.target.value);
    setImageError(false); // Reset error state when user types new url
  };

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setColor(event.target.value);
  };

  const generateRandomStreamName = () => {
    return "stream" + Math.floor(Math.random() * 100000).toString();
  };

  const addStreamDesign = async (
    streamName: string,
    image: string,
    bannerColor: string
  ) => {
    const apiUrl = "https://api.deafassistant.com/streamDesign/AddValues";

    const data = {
      streamName: streamName.toLowerCase(),
      uid: auth.currentUser?.uid,
      streamColor: bannerColor,
      logoUrl: image
    };


    try {
      axios.defaults.headers.post["Accept"] = "application/json; charset=utf-8";
      axios.defaults.headers.post["Content-Type"] = "application/json";

      await axios.post(apiUrl, data, await loginHeader());
    } catch (error) {
      console.error("There was an error!", error);
      if (error instanceof AxiosError) {
        alert(t("errorCreatingStream") + " " + error.response?.data + ". " + t("errorCreatingStreamDescription"));
      }
    }
  };

  const createStreamWithStyle = async (
    streamName: string,
    image: string,
    bannerColor: string
  ) => {
    const apiUrl = "https://api.deafassistant.com/stream/CreateStreamWithStyle";

    const finalName = streamName === "" ? generateRandomStreamName() : streamName.toLowerCase();
    const finalImage = image === "" ? "https://cdn-icons-png.flaticon.com/512/2395/2395089.png" : image;
    const finalColor = bannerColor === "" ? "#888888" : bannerColor;

    const data = {
      name: finalName,
      logoUrl: finalImage,
      bannerColor: finalColor,
    };

    const startTime = Date.now();

    try {
      axios.defaults.headers.post["Accept"] = "application/json; charset=utf-8";
      axios.defaults.headers.post["Content-Type"] = "application/json";

      const response = await axios.post(apiUrl, data);

      const endTime = Date.now();
      const timeTaken = endTime - startTime;

      if (timeTaken < 3000) {
        await new Promise((resolve) => setTimeout(resolve, 3000 - timeTaken));
      }

      navigate("/", { state: { responseData: response.data } });
    } catch (error) {
      console.error("There was an error!", error);
      setIsSubmitting(false);
      if (error instanceof AxiosError) {
        alert(
          t("errorCreatingStream") + " " +
          error.response?.data +
          ". " + t("errorCreatingStreamDescription")
        );
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (localError || isSubmitting) return;
    if (phoneProblem(phone) || eventDateProblem(eventDate)) {
      setShowDetailErrors(true);
      return;
    }

    setIsSubmitting(true);

    if (user) {
      if (name !== "") {
        try {
          const validationResponse = await axios.get(`https://api.deafassistant.com/streamdesign/IsStreamNameAllowed?streamName=${name}`);
          if (validationResponse.status === 200 && validationResponse.data === false) {
            // Continue
          }
        } catch (error: any) {
          setIsSubmitting(false);
          setLocalError(error.response?.data || "Invalid stream name.");
          return;
        }
      }

      const db = getDatabase(app);
      const userRef = ref(db, `users/${user.uid}`);

      const finalName = name === "" ? generateRandomStreamName() : name;
      const finalImage = imageLink === "" ? "https://cdn-icons-png.flaticon.com/512/2395/2395089.png" : imageLink;
      const finalColor = color === "" ? "#888888" : color;

      const formData = {
        uid: auth.currentUser?.uid,
        name: finalName,
        imageLink: finalImage,
        color: finalColor,
        // Only saved when given.
        ...(phone.trim() !== "" && { phone: phone.trim() }),
        ...(eventDate !== "" && { eventDate }),
      };

      try {
        await set(userRef, formData);
        await createStreamWithStyle(finalName, finalImage, finalColor);
        await addStreamDesign(finalName, finalImage, finalColor);
        await reload(); // the stream list is read from the API, which only now knows this stream
      } catch (error: any) {
        console.error("Error saving user details:", error.message);
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
    }
  };

  const getLogoSrc = () => {
    const sParam = arg.get("s");
    if (sParam === "khutbah" || sParam === "transit") {
      return "https://" + sParam + "captioning.com/images/logo-dark.svg";
    }
    return "https://conferencecaptioning.com/images/logo.png";
  };

  // Helper to ensure color picker receives valid hex or falls back to black
  // This prevents the color picker input from console warning on invalid text input
  const safeColorValue = /^#[0-9A-F]{6}$/i.test(color) ? color : "#888888";

  const isButtonDisabled = isSubmitting || localError.length > 0;

  return (
    <div className="min-h-screen bg-[#F6F6F6] font-sans text-gray-800">
      {isSubmitting ? (
        <div className="flex flex-col h-screen justify-center items-center animate-fade-in">
          <div className="w-32 md:w-48 mb-6">
            <StreamAnimation />
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold mb-2">{t("onboarding.generating")}</p>
            <p className="text-gray-500">
              {t("onboarding.generatingSubtitle")}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="w-full max-w-lg bg-white rounded-3xl shadow-xl p-8 md:p-10">

            {/* Logo Section */}
            <div className="flex justify-center mb-8">
              <img
                src={getLogoSrc()}
                alt="Logo"
                className="h-12 object-contain"
              />
            </div>

            {/* Header & Instructions */}
            <div className="text-center mb-8">
              <h1 className="font-bold text-2xl md:text-3xl text-gray-900 mb-2">
                {t("onboarding.title")}
              </h1>

              <button
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors font-medium flex items-center justify-center mx-auto gap-1"
                onClick={() => {
                  setShowModal(true);
                  trackClick("button_click-signup_instructions");
                }}
              >
                <span>{t("onboarding.instructions")}</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
              </button>
            </div>

            <InfoModal show={showModal} onClose={() => setShowModal(false)} />

            {/* Form Fields */}
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Stream Name Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1 ml-1">
                  Stream Name
                </label>
                <input
                  name="streamName"
                  placeholder={t("onboarding.streamNamePlaceholder") || "Enter unique stream name"}
                  value={name}
                  onChange={handleNameChange}
                  className={`w-full bg-gray-50 border rounded-xl text-base outline-none py-3 px-4 transition-all duration-200
                    ${localError
                      ? "border-red-500 focus:ring-2 focus:ring-red-200"
                      : "border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    }`}
                />
                {localError ? (
                  <p className="text-red-500 text-xs mt-1 ml-1 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    {localError}
                  </p>
                ) : (
                  <p className="text-gray-400 text-xs mt-1 ml-1">Leave empty to auto-generate.</p>
                )}
              </div>

              {/* Logo URL Input with Preview */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1 ml-1">
                  Logo URL <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <div className="flex gap-3 items-center">
                  <input
                    placeholder={t("onboarding.logoPlaceholder") || "https://example.com/logo.png"}
                    value={imageLink}
                    onChange={handleImageLinkChange}
                    className="flex-1 bg-gray-50 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl text-base outline-none text-gray-700 py-3 px-4 transition-all duration-200"
                  />

                  {/* Preview Logic */}
                  <div className="shrink-0 w-12 h-12 rounded-full border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center relative">
                    {imageLink && !imageError ? (
                      <img
                        src={imageLink}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={() => setImageError(true)}
                      />
                    ) : imageLink && imageError ? (
                      // Error Icon
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-red-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    ) : (
                      // Empty State Icon
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-300">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                    )}
                  </div>
                </div>
              </div>

              {/* Color Input with Color Picker */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1 ml-1">
                  Brand Color <span className="text-gray-400 font-normal">(Hex Code)</span>
                </label>
                <div className="relative">
                  {/* Hex Text Input */}
                  <input
                    placeholder={t("onboarding.colorPlaceholder") || "#888888"}
                    value={color}
                    onChange={handleColorChange}
                    className="w-full bg-gray-50 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl text-base outline-none text-gray-700 py-3 pl-4 pr-12 transition-all duration-200"
                  />

                  {/* Color Picker Interaction */}
                  <div className="absolute top-1/2 right-4 transform -translate-y-1/2 h-8 w-8 rounded-full border border-gray-300 shadow-sm overflow-hidden cursor-pointer hover:scale-105 transition-transform">
                    {/* The actual color input is expanded to fill the div but hidden via opacity, so clicks trigger the browser picker */}
                    <input
                      type="color"
                      value={safeColorValue}
                      onChange={handleColorChange}
                      className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] opacity-0 cursor-pointer p-0 m-0"
                    />
                    {/* The visual representation of the color */}
                    <div
                      className="w-full h-full pointer-events-none"
                      style={{ backgroundColor: color || '#888888' }}
                    />
                  </div>
                </div>
              </div>

              {/* Phone and event date (optional) */}
              <div>
                <label htmlFor="onboarding-phone" className="block text-sm font-semibold text-gray-700 mb-1 ml-1">
                  Phone Number <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  id="onboarding-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+1 555 123 4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => setShowDetailErrors(true)}
                  className={`w-full bg-gray-50 border rounded-xl text-base outline-none py-3 px-4 transition-all duration-200
                    ${showDetailErrors && phoneProblem(phone)
                      ? "border-red-500 focus:ring-2 focus:ring-red-200"
                      : "border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    }`}
                />
                {showDetailErrors && phoneProblem(phone) && (
                  <p role="alert" className="text-red-500 text-xs mt-1 ml-1">{phoneProblem(phone)}</p>
                )}
              </div>

              <div>
                <label htmlFor="onboarding-event-date" className="block text-sm font-semibold text-gray-700 mb-1 ml-1">
                  Event Date <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  id="onboarding-event-date"
                  name="eventDate"
                  type="date"
                  min={todayLocal()}
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  onBlur={() => setShowDetailErrors(true)}
                  className={`w-full bg-gray-50 border rounded-xl text-base outline-none py-3 px-4 transition-all duration-200
                    ${showDetailErrors && eventDateProblem(eventDate)
                      ? "border-red-500 focus:ring-2 focus:ring-red-200"
                      : "border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    }`}
                />
                {showDetailErrors && eventDateProblem(eventDate) ? (
                  <p role="alert" className="text-red-500 text-xs mt-1 ml-1">{eventDateProblem(eventDate)}</p>
                ) : (
                  <p className="text-gray-400 text-xs mt-1 ml-1">When do you need captioning? We'll use it to help you get ready.</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="text-center pt-4">
                <div className={`${isButtonDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}>
                  <Button
                    buttonText={buttonText}
                    onClick={handleSubmit}
                  />
                </div>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingPage;